<i18n>
    {
        "en": {},
        "ru": {
            "best_articles": "Лучшие статьи",
            "chained_pages": "Интересные подборки",
            "popular": "По популярности",
            "rating": "По рейтингу",
            "alphabet": "По алфавиту",
            "placeholder": "Введите название бренда...",
            "perfume_one": "аромат",
            "perfume_two": "аромата",
            "perfume_three": "ароматов",
            "brand_one": "бренд",
            "brand_two": "бренда",
            "brand_three": "брендов",
            "countries_one": "страна",
            "countries_two": "страны",
            "countries_three": "стран",
            "by_filter": "(по фильтру)"
        }
    }
</i18n>

<template>
    <div class="country-brands-page">

        <block-price-panel />

        <div class="country-brands-page__block">
            <transition name="fade">
                <ModalFilter
                    v-show="filterIsShowed"
                    :disable-modal-parent-scroll="filterIsShowed"
                    @close-sidebar="filterIsShowed = false"
                    :isOpened="filterIsShowed"
                    :filtersCount="filtersCount"
                    :params="filterParams">
                    <template #filter-body>
                        <BrandsFilter 
                            ref="filter"
                            :fields="filterFields" 
                            :settedValues="filterParams" 
                            :additionalValues="{q: searchQuery}"
                            :isModal="true"
                            @change="filtersCount = $event.count"
                            @submit="onFilterSubmit"
                            @reset="onFilterReset"
                            class="cat-filter_modal" />
                    </template>
                </ModalFilter>
            </transition>

            <PageBlock :content-visible="isDeviceDesktop">
                <Breadcrumb
                    show-prev-button
                    prev-path-name="Regions"
                    :items="breadcrumbs"/>

                <SectionCard :loaded="loaded">
                    <template #main>

                        <PageBlock
                            class="country-brands-page__section-block"
                            id="country_brands"
                            :small-header="!isDeviceDesktop"
                            :content-visible="isDeviceDesktop"
                            :title="title"
                            :title-picture="get(country, 'flag.url')"
                            :caption-count="brandsTotal | numberFormat"
                            :caption="countryBrandsCaption"
                            :description="description"
                            :heading-tag="'h1'">

                            <CatalogList
                                id="catalog"
                                class="country-brands-page__list"
                                :grid="isDeviceDesktop"
                                :grid-columns="5"
                                :filters-count="filtersCount"
                                :sortInSearchField="isDeviceDesktop"
                                :filterInSearchField="isDeviceDesktop"
                                :placeholder="$t('placeholder')"
                                with-filter
                                with-sort
                                :search-loading="queryPending"
                                :query="searchQuery"
                                @search="searchQuery = $event"
                                @show-filter="filterIsShowed = true"
                                @change-sort-type="onChangeSortType"
                                @change-sort-order="onChangeSortOrder"
                                @reset-all="resetAll"
                                :with-pagination="parseInt(catalogLastPage) > 1"
                                :items="catalogItems"
                                :sort-options="sortOptions"
                                v-model="catalogPage"
                                :page-count="catalogLastPage"
                                :sort-order="sortOrder"
                                :sort-type="sortType">

                                <template #item="{ item }" >

                                    <BlockShop
                                        :direction="isDeviceDesktop ? 'column' : 'row'"
                                        with-rating
                                        :name="get(item, 'name')"
                                        :rating="get(item, 'ratings.value', -1)"
                                        :picture="get(item, 'logo.url')"
                                        :url="get(item, 'htmlUrl')"
                                        :caption="getBrandCaption(item)"/>

                                </template>

                            </CatalogList>

                        </PageBlock>
                    </template>
                </SectionCard>

            </PageBlock>

            <PageBlock v-if="showRelatedArticles" :title="$t('best_articles')">
                <div class="perfume__page-block">
                    <ArticleRowItem
                        :key="index + article.code"
                        v-for="(article, index) in relatedArticles"
                        :data="article"/>
                </div>
            </PageBlock>
            <PageBlock v-if="showRelatedPages" :title="$t('chained_pages')">
                <div class="perfume__page-block">
                    <LinkedPagesList :items="relatedPages"/>
                </div>
            </PageBlock>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import get from 'lodash/get';
import url from '@/filters/url';

import Breadcrumb from '@/components/layout/nav/Breadcrumb'
import BlockShop from '@/components/catalog/BlockShop';
import SectionCard from '@/components/layout/AppSectionCard';
import PageBlock from '@/components/layout/AppPageBlock';

import Country from '@/models/catalog/Country'
import Brand from '@/models/catalog/brand'
import Field from '@/models/services/field'
import Filter from '@/models/services/filter'

import ArticleRowItem from '@/components/layout/blocks/ArticlesList/ArticleRowItem';
import LinkedPagesList from '@/components/layout/blocks/LinkedPagesList';
import BrandsFilter from '@/components/catalog/filters/BrandsFilter';
import ModalFilter from '@/components/catalog/filters/parts/ModalFilter';
import BlockPricePanel from '@/components/catalog/BlockPricePanel';
import CatalogList from '@/components/catalog/CatalogList';

import { getApiQueryParams, getCatalogQuery, CatalogListMixin } from '@/mixins/catalog/catalogList.mixin';

export default {
    name: 'CountryBrands',
    components: {
        LinkedPagesList,
        ArticleRowItem,
        SectionCard,
        PageBlock,
        BlockShop,
        Breadcrumb,
        BrandsFilter,
        ModalFilter,
        BlockPricePanel,
        CatalogList
    },
    mixins: [CatalogListMixin],
    asyncData({ params, $auth, app, query, route, error }) {

        const baseUrl = app.i18n.__baseUrl
        const catalogHref = app.router.resolve({
            name: 'Regions'
        }).href

        const countryModel = new Country({
            code: params.country
        });

        // Иницииализируем модели
        const brand = new Brand()
        const filter = new Filter()
        const field = new Field()
        const filterFields = new Field().custom('services', filter, brand, field)

        // массив фильтров из query
        const catalogParams = getCatalogQuery(query, false, 'q');

        const catalogPage = url.getPageNumber(route.path);

        const searchQuery = getApiQueryParams(query, {}, 'q').q;
        const sortType = getApiQueryParams(query).sort ? getApiQueryParams(query).sort : 'popularity';
        const sortOrder = getApiQueryParams(query).order || 'desc';

        const perPage = 100
        const filterParams = catalogParams.filters || {};

        filterParams.country = params.country
        
        // Формируем параметры для запроса за ароматами
        const brandParams = {
            q: searchQuery,
            page: catalogPage,
            sort: sortType,
            order: sortOrder,
            per_page: perPage,
            ...filterParams
        };

        const requests = [
            (new Country).find(params.country),
            countryModel.relatedArticles().get(),
            countryModel.relatedPages().get(),
            brand.for(countryModel).params(url.restQueryObject(brandParams)).get(),
            filterFields.get(),
        ];

        return Promise.all(requests).then((results) => {
            const country = get(results, '[0]', {});

            const breadcrumbs = [
                {
                    path: baseUrl,
                    name: app.i18n.t('breadcrumbs.perfumes'),
                },
                {
                    path: baseUrl + catalogHref,
                    name: app.i18n.t('breadcrumbs.regions'),
                },
                {
                    path: get(country, 'region.htmlUrl'),
                    name: get(country, 'region.name')
                },
                {
                    path: country.htmlUrl,
                    name: country.name
                }
            ];

            if (country.region.code !== params.region) {
                error({statusCode: 404});
            }

            const relatedArticles = results[1];
            const relatedPages = results[2];
            const brands = get(results, '[3][0]');
            const catalogItems = get(brands, 'items');
            const catalogLastPage = get(brands, 'lastPage')
            const countryBrandTotal = get(brands, '[total]')
            const filterFields = get(results, '[4][0]')
            if (filterFields.countries) {
                delete filterFields.countries;
            }

            if (process.server) {
                app.store.dispatch('rest/headers/filterLastModified', {
                    url: [
                        `/countries/${country.code}`
                    ]
                });
            }


            return {
                brandsTotal: countryBrandTotal,
                countryBrandTotal,
                filterFields,
                catalogPage,
                searchQuery,
                catalogLastPage,
                sortType,
                sortOrder,
                country,
                brands,
                catalogItems,
                relatedArticles,
                relatedPages,
                breadcrumbs,
                filterParams,
                filters: filterParams,
                staticFilters: ['country']

            }
        }).catch((e) => {
            error({statusCode: e.response.status, message: e.response.data});
        });
    },
    data() {
        return {
            queryPending: false,            
            filterFields: {},
            filterParams: {},
            filtersCount: 0,
            filters: {},
            searchQuery: null,
            q: null,
            nameProp: 'q',
            loaded: false,
            breadcrumbs: [],
            relatedArticles: null,
            catalogLastPage: 1,
            relatedPages: null,
            catalogPage: 1,
            sortType: 'popularity',
            sortOrder: 'desc',
            sortOptions: {
                popularity: {
                    name: this.$t('popular')
                },
                rating: {
                    name: this.$t('rating')
                },
                name: {
                    name: this.$t('alphabet')
                },
            },
            countryBrandTotal: 0,
            brandsTotal: 0,
            catalogItems: [],
            brands: null,
            filterIsShowed: false
        }
    },
    computed: {
        ...mapGetters(['isDeviceDesktop']),
        catalogQueryObject() {
            const country = new Country({code: this.$route.params.country})

            return new Brand().for(country)
        },
        title() {
            return get(this.country, 'meta.h1')
        },
        description() {
            return get(this.country, 'description')
        },
        countryBrandsCaption() {
            let text = this.$options.filters.numberDeclension(
                this.brandsTotal, 
                this.$t('declensions.brands'), 
                false
            );

            if (this.q || this.searchQuery || Object.keys(this.filters).length > 1) {
                text += ' ' + this.$t('by_filter');
            };

            return text;
        },
        showRelatedPages() {
            return this.relatedPages && this.relatedPages.length && !this.isDeviceDesktop
        },
        showRelatedArticles() {
            return this.relatedArticles && this.relatedArticles.length && !this.isDeviceDesktop
        }
    },
    watch: {
        itemsTotal(value) {
            this.brandsTotal = value;
        },
    },
    mounted() {
        this.loaded = true;
        this.filtersCount = this.$refs.filter.filtersCount;
    },
    methods: {
        get,
        getBrandCaption(item) {
            const total = get(item, 'perfumes.total')

            if (total) {
                return this.$options.filters.numberDeclension(total, this.$t('declensions.perfumes'))
            }
        }
    },
    head() {
        return {
            title: get(this.country, 'meta.title', this.country.name) + this.pageHeadCounter,
            meta: this.$options.filters.metaApiExtract(this.country),
            link: [
                {
                    hid: 'i18n-can',
                    rel: 'canonical',
                    href: get(this.country, 'meta.canonical')
                }
            ]
        }
    }
}
</script>

<style lang="less">
.country-brands-page {

    &__region {
        border-top: rem(1) solid @color-gray-white;
    }

    &__section-block {
        margin-bottom: 0;

        @{desktop} & {
            --desktop-header-padding-top: rem(20);
        }
    }
}
</style>
